// Shared ChordPro helpers: the metadata header shown while editing a song, and
// the chords-over-lyrics rendering shown on the song page.

// "{title: X}\n{key: C}\n..." for the fields that have a value. The title line
// is always present (empty until typed) so the preview never looks blank.
export function chordProMetadata({ title = "", key = "", time = "", tempo = "" }) {
  let out = `{title: ${title}}\n`;
  if (key) out += `{key: ${key}}\n`;
  if (time) out += `{time: ${time}}\n`;
  if (tempo) out += `{tempo: ${tempo}}\n`;
  return out;
}

const SECTION_START = /^(?:start_of_|so)(verse|chorus|bridge|tab|grid|v|c|b|t|g)\b/;
const SECTION_END = /^(?:end_of_|eo)/;
const SECTION_NAMES = { v: "verse", c: "chorus", b: "bridge", t: "tab", g: "grid" };
const COMMENT_DIRECTIVES = new Set(["comment", "c", "comment_italic", "ci", "comment_box", "cb"]);
const CHORD_MARKER = /\[([^\]]*)\]/g;

// Splits one lyric line into [{ chord, text }] pairs: each chord with the text
// that follows it up to the next chord. Text before the first chord has no
// chord.
export function splitChords(line) {
  const segments = [];
  let last = 0;
  let chord = null;
  for (const match of line.matchAll(CHORD_MARKER)) {
    const text = line.slice(last, match.index);
    if (chord !== null || text !== "") segments.push({ chord, text });
    chord = match[1];
    last = match.index + match[0].length;
  }
  const rest = line.slice(last);
  if (chord !== null || rest !== "" || segments.length === 0) segments.push({ chord, text: rest });
  return segments;
}

// Parses ChordPro source into display rows:
//   { type: "section", label }  { type: "comment", text }
//   { type: "blank" }           { type: "line", segments, hasChords }
// Metadata directives (title, key, ...) are dropped: the page shows them.
export function parseChordPro(source) {
  const rows = [];
  for (const raw of String(source ?? "").replace(/\r/g, "").split("\n")) {
    const line = raw.trimEnd();
    if (line.startsWith("#")) continue;

    const directive = line.trim().match(/^\{\s*([^}:]*?)\s*(?::\s*(.*?))?\s*\}$/);
    if (directive) {
      const name = directive[1].toLowerCase();
      const value = directive[2] ?? "";
      const start = name.match(SECTION_START);
      if (start) {
        const kind = SECTION_NAMES[start[1]] ?? start[1];
        rows.push({ type: "section", label: value || kind.charAt(0).toUpperCase() + kind.slice(1) });
      } else if (COMMENT_DIRECTIVES.has(name)) {
        rows.push({ type: "comment", text: value });
      } else if (SECTION_END.test(name)) {
        rows.push({ type: "blank" });
      }
      continue;
    }

    if (line.trim() === "") {
      rows.push({ type: "blank" });
      continue;
    }

    const segments = splitChords(line);
    rows.push({ type: "line", segments, hasChords: segments.some((s) => s.chord !== null) });
  }
  return rows;
}

// Builds the chords-over-lyrics DOM for ChordPro source (text only, no HTML
// injection). Each chord sits in a column directly above the words it
// precedes.
export function renderChordPro(source) {
  const root = document.createElement("div");
  root.className = "chordpro";

  let previousBlank = true;
  for (const row of parseChordPro(source)) {
    if (row.type === "blank") {
      if (!previousBlank) root.appendChild(el("div", "h-3"));
      previousBlank = true;
      continue;
    }
    previousBlank = false;

    if (row.type === "section") {
      root.appendChild(el("div", "mt-2 text-xs font-semibold uppercase tracking-wide text-fg-muted", row.label));
    } else if (row.type === "comment") {
      root.appendChild(el("div", "italic text-fg-muted", row.text));
    } else if (!row.hasChords) {
      root.appendChild(el("div", "whitespace-pre-wrap", row.segments.map((s) => s.text).join("")));
    } else {
      const line = el("div", "flex flex-wrap items-end");
      for (const { chord, text } of row.segments) {
        const column = el("span", "inline-flex flex-col");
        column.appendChild(el("span", "min-h-[1.25em] pr-1 font-mono text-sm font-bold text-link", chord ?? ""));
        column.appendChild(el("span", "whitespace-pre", text));
        line.appendChild(column);
      }
      root.appendChild(line);
    }
  }
  return root;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
